import { logger, getBaliDateKey, toBaliDateKey, diffBaliDays } from '@/lib';
import {
  MEMBERSHIP_DB_STATUS,
  MEMBERSHIP_DISPLAY_STATUS,
  type MembershipDateRange,
  type MembershipDisplayStatus,
  type MembershipStatus,
} from './constants';

/**
 * Derive the display status from a membership's DB status + dates.
 * Cancelled takes priority over date-based status.
 */
export const getMembershipStatus = (
  membership: MembershipDateRange,
  referenceDate?: Date
): MembershipDisplayStatus => {
  if (membership.status === MEMBERSHIP_DB_STATUS.CANCELLED) {
    return MEMBERSHIP_DISPLAY_STATUS.CANCELLED;
  }

  const { start_date, end_date } = membership;
  if (!start_date || !end_date) return MEMBERSHIP_DISPLAY_STATUS.EXPIRED;

  try {
    // start_date/end_date are Bali calendar days stored as `date` columns
    // (YYYY-MM-DD). Compare them as Bali date keys against *today in Bali* so a
    // membership never flips active↔expired based on the admin's browser
    // timezone — the pre-migration Date math bucketed "today" to browser-local
    // midnight, which read a day early before 08:00 WITA and for any admin
    // outside Asia/Makassar.
    const startKey = toBaliDateKey(start_date);
    const endKey = toBaliDateKey(end_date);
    const todayKey = getBaliDateKey(referenceDate ?? new Date());

    if (!startKey || !endKey || !todayKey) return MEMBERSHIP_DISPLAY_STATUS.EXPIRED;

    if (endKey < todayKey) return MEMBERSHIP_DISPLAY_STATUS.EXPIRED;
    if (startKey > todayKey) return MEMBERSHIP_DISPLAY_STATUS.UPCOMING;
    return MEMBERSHIP_DISPLAY_STATUS.ACTIVE;
  } catch (error) {
    logger.error('Error calculating membership status', error);
    return MEMBERSHIP_DISPLAY_STATUS.EXPIRED;
  }
};

/** Preferred name for new code. */
export const getMembershipDisplayStatus = getMembershipStatus;

export const isMembershipCancelled = (membership: MembershipDateRange): boolean =>
  membership.status === MEMBERSHIP_DB_STATUS.CANCELLED;

export const isMembershipActive = (membership: MembershipDateRange, referenceDate?: Date): boolean =>
  getMembershipStatus(membership, referenceDate) === MEMBERSHIP_DISPLAY_STATUS.ACTIVE;

export const isMembershipExpired = (membership: MembershipDateRange, referenceDate?: Date): boolean =>
  getMembershipStatus(membership, referenceDate) === MEMBERSHIP_DISPLAY_STATUS.EXPIRED;

export const isMembershipUpcoming = (membership: MembershipDateRange, referenceDate?: Date): boolean =>
  getMembershipStatus(membership, referenceDate) === MEMBERSHIP_DISPLAY_STATUS.UPCOMING;

/**
 * Days remaining in an active membership (inclusive of today).
 * Returns null if membership is not active.
 */
export const calculateDaysRemaining = (
  membership: MembershipDateRange,
  referenceDate?: Date
): number | null => {
  if (getMembershipStatus(membership, referenceDate) !== MEMBERSHIP_DISPLAY_STATUS.ACTIVE) {
    return null;
  }

  const { end_date } = membership;
  if (!end_date) return null;

  try {
    const endKey = toBaliDateKey(end_date);
    const todayKey = getBaliDateKey(referenceDate ?? new Date());
    if (!endKey || !todayKey) return null;

    // +1 so "today" counts as a remaining day
    const daysRemaining = diffBaliDays(todayKey, endKey) + 1;
    return Math.max(0, daysRemaining);
  } catch (error) {
    logger.error('Error calculating days remaining', error);
    return null;
  }
};

/**
 * Server-side compatible status calculation from raw date strings.
 * Matches the logic used in database triggers/functions.
 */
export const getMembershipStatusFromStrings = (
  startDateStr: string,
  endDateStr: string,
  referenceDateStr?: string
): MembershipStatus => {
  const membership = { start_date: startDateStr, end_date: endDateStr };
  const referenceDate = referenceDateStr ? new Date(referenceDateStr) : undefined;
  return getMembershipStatus(membership, referenceDate);
};
